<script setup lang="ts">
import { computed, ref, type PropType } from 'vue';
import type { PopoverPlacement } from 'naive-ui';
import GaiaButton from '../base/GaiaButton.vue';
import GaiaPopover from '../base/GaiaPopover.vue';
import GaiaText from '../base/GaiaText.vue';
import { gaiaNeutral, gaiaShadow, gaiaStatus } from '../themes/tokens';

const props = defineProps({
  /** Bold lead-in — the question itself, e.g. "Delete this run?". */
  title: {
    type: String,
    default: undefined,
  },
  confirmLabel: {
    type: String,
    default: 'Delete',
  },
  cancelLabel: {
    type: String,
    default: 'Cancel',
  },
  placement: {
    type: String as PropType<PopoverPlacement>,
    default: 'bottom-end',
  },
  /** px. Long copy wraps rather than stretching the bubble across the page. */
  width: {
    type: Number,
    default: 268,
  },
  /**
   * danger: the destructive confirm (alert surface, error button). neutral: a
   * plain question before a non-destructive step — "Switch organisation?".
   */
  tone: {
    type: String as PropType<'danger' | 'neutral'>,
    default: 'danger',
  },
  /**
   * v-model:show. Leave unbound and the trigger's click opens the bubble; bind
   * it to open the bubble from elsewhere — a table row click whose bubble
   * hangs off one of its cells.
   */
  show: {
    type: Boolean as PropType<boolean | undefined>,
    default: undefined,
  },
});

const emit = defineEmits<{ confirm: []; cancel: []; 'update:show': [value: boolean] }>();

// The bubble is ours to close: naive's click trigger opens it, but a decision
// has to dismiss it too, or the popover outlives the row it was asking about.
const internalShow = ref(false);
const show = computed(() => props.show ?? internalShow.value);

function setShow(value: boolean): void {
  internalShow.value = value;
  emit('update:show', value);
}

function onConfirm(): void {
  setShow(false);
  emit('confirm');
}

function onCancel(): void {
  setShow(false);
  emit('cancel');
}

/**
 * Inline rather than a scoped `v-bind()` block: the popover teleports the
 * bubble to <body>, while `v-bind()` writes its custom properties onto this
 * component's root — which is the trigger, left behind. The bubble would
 * inherit none of them, and `raw` mode means naive draws no chrome of its own,
 * so it rendered as bare text over the page. Inline is enough here because the
 * bubble is one element; teleported content whose *descendants* need tokens
 * declares them as custom properties instead — see GaiaChatSettings.
 */
const bubbleStyle = computed(() => ({
  width: `${props.width}px`,
  padding: '11px 12px',
  borderRadius: '10px',
  border: `1px solid ${props.tone === 'danger' ? gaiaStatus.alertBorder : gaiaNeutral.borderCard}`,
  background: props.tone === 'danger' ? gaiaStatus.alertSurface : gaiaNeutral.surface,
  boxShadow: gaiaShadow.menu,
}));
</script>

<template>
  <!-- A confirm (destructive by default) anchored to the control that asked for it — the
       floating sibling of GaiaConfirmCard and GaiaTableRowState's confirm
       strip, and dressed like them: the tinted danger surface, the alert
       hairline, Cancel then a filled error button. tone="neutral" trades the
       danger dress for a plain surface and a primary button, for questions that
       destroy nothing — "Switch organisation?".

       Reach for it where a row strip cannot go: a pane header, or a grid whose
       columns scroll sideways so a strip would sit against unrelated cells. In
       an ordinary list the strip is still the redesign's answer, because it
       says which row it is about without covering it. -->
  <gaia-popover
    :show="show"
    :trigger="props.show === undefined ? 'click' : 'manual'"
    :placement="props.placement"
    @update:show="setShow"
    @clickoutside="props.show !== undefined && setShow(false)"
  >
    <template #trigger>
      <slot name="trigger" />
    </template>
    <n-flex vertical :size="9" :style="bubbleStyle">
      <gaia-text v-if="props.title" size="dense" weight="semibold" tone="ink">
        {{ props.title }}
      </gaia-text>
      <gaia-text v-if="$slots.default" size="dense" tone="text" :style="{ lineHeight: 1.5 }">
        <slot />
      </gaia-text>
      <!-- Trailing, unlike the card's leading pair: the bubble is narrow and
           hangs off a right-hand control, so the buttons meet the pointer. -->
      <n-flex justify="end" :size="8" :wrap="false">
        <gaia-button size="small" @click="onCancel">{{ props.cancelLabel }}</gaia-button>
        <gaia-button
          size="small"
          :type="props.tone === 'danger' ? 'error' : 'primary'"
          @click="onConfirm"
        >
          {{ props.confirmLabel }}
        </gaia-button>
      </n-flex>
    </n-flex>
  </gaia-popover>
</template>
